import React from "react";
import { CellContext } from "@tanstack/react-table";
import styled, { CSSProp } from "styled-components";

import { useLocale } from "@adsk/alloy-react-locale";
import Theme from "@adsk/alloy-react-theme";
import { OverflowTooltip } from "@adsk/alloy-react-tooltip";

import {
  EntityWithCategoryAndStatus,
  StatusColor,
  statusColors,
  UNSPECIFIED_STATUS_COLOR,
  UNSPECIFIED_STATUS_COLOR_KEY,
} from "../../../models";

type StatusColorIndicatorProps = {
  disabled?: boolean;
  color: StatusColor;
  style?: CSSProp;
};

export const StatusName = styled.div<{ isUnspecified: boolean }>`
  display: inline-flex;
  ${(props) =>
    props.isUnspecified &&
    `color: ${Theme.colors.charcoal500}; font-style: italic;`}
`;

const StatusCellContainer = styled.div`
  align-items: center;
  display: flex;
  overflow: hidden;
`;

export const StatusColorIndicator = styled.div<StatusColorIndicatorProps>`
  background-color: ${({ color }) =>
    statusColors[color] || UNSPECIFIED_STATUS_COLOR};
  border-radius: 2px;
  flex-shrink: 0;
  margin-right: 8px;
  opacity: ${({ disabled }) => (disabled ? 0.35 : undefined)};
  height: 16px;
  width: 4px;
`;

export const StatusCellData = <T,>({
  entity,
  disabled,
}: {
  entity: EntityWithCategoryAndStatus<T>;
  disabled?: boolean;
}) => {
  const { T: localeT } = useLocale(
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    ({ locale }) => require(`../../i18n/${locale}.json`)
  );

  const isValidStatus =
    entity.category?.statusSetId === entity.status?.statusStepSetId;
  const statusColor =
    entity.status && isValidStatus
      ? entity.status.color
      : UNSPECIFIED_STATUS_COLOR_KEY;
  const statusName =
    entity.status && isValidStatus
      ? entity.status.label
      : localeT.translate("UNSPECIFIED");

  return (
    <StatusCellContainer>
      <StatusColorIndicator color={statusColor} disabled={disabled} />
      <StatusName isUnspecified={!entity.status || !isValidStatus}>
        <OverflowTooltip
          getTargetProps={() => ({ style: { overflow: "hidden" } })}
        >
          {statusName}
        </OverflowTooltip>
      </StatusName>
    </StatusCellContainer>
  );
};

const StatusCell = <T,>({
  row,
}: CellContext<EntityWithCategoryAndStatus<T>, string>) => {
  return (
    <StatusCellData disabled={!row.getCanSelect()} entity={row.original} />
  );
};

export default StatusCell;
